import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { TUser } from "../services/auth/types";

interface AuthContextType {
  user: TUser | null;
  token: string | null;
  tokenExpires: number | null;
  login: (user: TUser, token: string, tokenExpires: number) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const readStoredUser = (): TUser | null => {
  try {
    const savedUser = localStorage.getItem("user");
    return savedUser ? (JSON.parse(savedUser) as TUser) : null;
  } catch {
    return null;
  }
};

const readStoredTokenExpires = (): number | null => {
  const savedTokenExpires = localStorage.getItem("tokenExpires");
  return savedTokenExpires ? Number(savedTokenExpires) : null;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  // Restore auth state synchronously so the first render is already
  // authenticated on refresh (otherwise ProtectedRoute redirects to login).
  const [user, setUser] = useState<TUser | null>(() => readStoredUser());
  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem("token")
  );
  const [tokenExpires, setTokenExpires] = useState<number | null>(() =>
    readStoredTokenExpires()
  );

  const [tokenExpired, setTokenExpired] = useState(false);

  useEffect(() => {
    if (!tokenExpires) {
      return;
    }

    const check = () => setTokenExpired(Date.now() > tokenExpires);

    const timeout = setTimeout(check, 0);
    const id = setInterval(check, 30_000);
    return () => {
      clearTimeout(timeout);
      clearInterval(id);
    };
  }, [tokenExpires]);

  const login = (userData: TUser, token: string, tokenExpires: number) => {
    const keysToPreserve = new Set(["utm_sent", "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "referrer", "promoName", "promoPosition", "promoCreative"]);
    const allKeys = Object.keys(localStorage);
    allKeys.forEach((key) => {
      if (!keysToPreserve.has(key)) {
        localStorage.removeItem(key);
      }
    });

    setUser(userData);
    setToken(token);
    setTokenExpires(tokenExpires);
    setTokenExpired(false);

    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("token", token);
    localStorage.setItem("tokenExpires", tokenExpires.toString());
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setTokenExpires(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("tokenExpires");
  };

  const isAuthenticated = !!token && !tokenExpired;

  return (
    <AuthContext.Provider
      value={{ user, token, tokenExpires, login, logout, isAuthenticated }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
